import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type MarqueeBox = { active: boolean; x: number; y: number; w: number; h: number };

type Options = {
  dragThreshold?: number; // px
  isInteractive?: (el: HTMLElement) => boolean;
  itemSelector: string; // e.g. '[data-item]'
  getItemKey?: (el: HTMLElement) => string | null; // default: el.getAttribute('data-path')
};

type ApplySelectionFn = (keys: string[], mode: 'replace' | 'add') => void;

export function useMarqueeSelection(
  containerRef: React.RefObject<HTMLElement | null>,
  applySelection: ApplySelectionFn,
  opts: Options
) {
  const {
    dragThreshold = 4,
    isInteractive = (el) =>
      !!el.closest('button, a, input, textarea, select, [role="button"], .form-check-input, .dropdown, .btn'),
    itemSelector,
    getItemKey = (el) => el.getAttribute('data-path'),
  } = opts;

  const [box, setBox] = useState<MarqueeBox>({ active: false, x: 0, y: 0, w: 0, h: 0 });
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const draggingRef = useRef(false);
  const addingModeRef = useRef<'replace' | 'add'>('replace');

  const getRect = useCallback((x1: number, y1: number, x2: number, y2: number) => {
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const width = Math.abs(x1 - x2);
    const height = Math.abs(y1 - y2);
    return { left, top, width, height };
  }, []);

  const intersects = useCallback((a: DOMRect, b: { left: number; top: number; width: number; height: number }) => {
    return a.left < b.left + b.width && a.right > b.left && a.top < b.top + b.height && a.bottom > b.top;
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof HTMLElement)) return;
      if (e.button !== 0) return; // solo botón izquierdo
      if (isInteractive(e.target)) return; // ni sobre controles interactivos

      addingModeRef.current = e.ctrlKey || e.metaKey ? 'add' : 'replace';
      startRef.current = { x: e.clientX, y: e.clientY };
      draggingRef.current = false;
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!startRef.current) return;
      const dx = e.clientX - startRef.current.x;
      const dy = e.clientY - startRef.current.y;
      const dist = Math.max(Math.abs(dx), Math.abs(dy));
      if (!draggingRef.current) {
        if (dist < dragThreshold) return;
        draggingRef.current = true;
        document.body.style.userSelect = 'none';
      }
      const r = getRect(startRef.current.x, startRef.current.y, e.clientX, e.clientY);
      setBox({ active: true, x: r.left, y: r.top, w: r.width, h: r.height });
      e.preventDefault();
    };

    const onPointerUp = () => {
      if (!startRef.current) return;
      startRef.current = null;

      if (!draggingRef.current) {
        setBox((b) => ({ ...b, active: false, w: 0, h: 0 }));
        return;
      }

      draggingRef.current = false;
      document.body.style.userSelect = '';

      setBox((prev) => {
        const { x, y, w, h } = prev;
        const finalRect = { left: x, top: y, width: w, height: h };
        const container = containerRef.current;
        const els = container ? (Array.from(container.querySelectorAll(itemSelector)) as HTMLElement[]) : [];
        const hits: string[] = [];
        for (const el of els) {
          const rect = el.getBoundingClientRect();
          if (intersects(rect, finalRect)) {
            const key = getItemKey(el);
            if (key) hits.push(key);
          }
        }
        if (hits.length > 0) {
          applySelection(hits, addingModeRef.current);
        }
        return { active: false, x: 0, y: 0, w: 0, h: 0 };
      });
    };

    root.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      root.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove as any);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [containerRef, applySelection, getRect, intersects, isInteractive, itemSelector, dragThreshold]);

  const overlayStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'fixed',
      left: box.x,
      top: box.y,
      width: box.w,
      height: box.h,
      background: 'rgba(37, 99, 235, 0.15)',
      border: '2px solid rgba(37, 99, 235, 0.8)',
      borderRadius: 4,
      pointerEvents: 'none',
      zIndex: 99999,
    }),
    [box.x, box.y, box.w, box.h]
  );

  return { box, overlayStyle };
}
